import { useEffect, useState } from "react";
import { useDataProvider, useRecordContext, useRedirect, useTranslate } from "ra-core";
import { useNavigate } from "react-router";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ShowButton } from "@/components/admin/show-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { PointsLedger } from "./PointsLedger";
import { ORDER_STATUSES } from "../orders/orderUtils";

// ─── Contact Orders Section ───────────────────────────────────────────────────
type OrderRow = {
  id: number;
  order_number: string | null;
  status: string;
  total: number;
  currency: string;
  created_at: string;
};

const ContactOrdersSection = () => {
  const record = useRecordContext<Contact>();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    setLoading(true);
    dataProvider
      .getList("orders", {
        filter: { "contact_id@eq": record.id },
        sort: { field: "created_at", order: "DESC" },
        pagination: { page: 1, perPage: 2 },
      })
      .then(({ data }) => {
        setOrders(data as OrderRow[]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [record?.id, dataProvider]);

  if (loading)
    return <p className="text-xs text-muted-foreground">Loading...</p>;
  if (!orders.length)
    return <p className="text-xs text-muted-foreground">暂无订单</p>;

  return (
    <div className="space-y-1.5">
      {orders.map((o) => {
        const statusLabel =
          ORDER_STATUSES.find((s) => s.value === o.status)?.label ?? o.status;
        return (
          <div
            key={o.id}
            className="flex items-center justify-between gap-2 py-1 cursor-pointer hover:text-primary"
            onClick={() => redirect("show", "orders", o.id)}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-mono text-primary truncate">
                {o.order_number ?? `ORD-${String(o.id).padStart(4, "0")}`}
              </span>
              <Badge
                variant="outline"
                className="text-[10px] h-4 px-1 shrink-0"
              >
                {statusLabel}
              </Badge>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              {o.currency}{" "}
              {o.total.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── ContactAside ─────────────────────────────────────────────────────────────
export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const redirect = useRedirect();
  const navigate = useNavigate();

  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
      </div>

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <AsideSection title="客户画像">
        <TagsListEdit />
      </AsideSection>

      <AsideSection title="订单管理">
        <ContactOrdersSection />
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full text-xs"
          onClick={() =>
            navigate(
              `/orders?filter=${encodeURIComponent(
                JSON.stringify({ "contact_id@eq": record.id }),
              )}`,
            )
          }
        >
          <ExternalLink className="size-3 mr-1.5" />
          进入订单系统
        </Button>
      </AsideSection>

      <AsideSection title="Member & Points">
        <PointsLedger />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};
